import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MessageCircle } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import VerifiedBadge from "../components/VerifiedBadge";

function profileImage(profile) {
  if (profile.photos?.length > 0) return fileUrl(profile.photos[0]);
  if (profile.photo_url) return profile.photo_url;
  return null;
}

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches");
        setMatches(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="matches-page">
      <div className="mb-12">
        <div className="text-gold-soft tracking-overline text-xs mb-2">YOUR MATCHES</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light">People who chose you back.</h1>
      </div>

      {loading ? (
        <div className="text-gold-soft tracking-overline text-xs">LOADING</div>
      ) : matches.length === 0 ? (
        <div className="glass-strong rounded-3xl p-12 text-center" data-testid="matches-empty">
          <Heart className="mx-auto text-gold mb-6" size={32} />
          <h2 className="font-display text-2xl font-light mb-3">No matches yet — that's okay.</h2>
          <p className="text-white/55 leading-relaxed max-w-md mx-auto">
            Good things take time. Head to Discover and let connection find you.
          </p>
          <Link to="/discover" className="btn-gold inline-block mt-8" data-testid="goto-discover">
            Start discovering
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {matches.map((m, i) => (
            <motion.div
              key={m.match_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="group relative rounded-3xl overflow-hidden glass aspect-[3/4] cursor-pointer"
              data-testid={`match-card-${i}`}
            >
              {profileImage(m.profile) ? (
                <img src={profileImage(m.profile)} alt={m.profile.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
                  <span className="font-display text-5xl text-gold/40">{m.profile.name?.[0]}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5">
                <div className="flex items-center gap-1.5">
                  <div className="font-display text-xl">{m.profile.name}, <span className="text-white/70">{m.profile.age}</span></div>
                  {m.profile.verified && <VerifiedBadge size={14} />}
                </div>
                <div className="text-xs text-white/60 mt-1">{m.profile.location}</div>
                <Link to="/messages" className="mt-3 inline-flex items-center gap-1.5 text-xs text-gold tracking-wide">
                  <MessageCircle size={12} /> Say hello
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
